import { Document, Gallery, Video } from 'iconsax-react';
import type { ReactNode } from 'react';
import AudioIcon from '@/assets/images/icons/vector.svg?react';
import DurationDisplay from '@/components/common/duration-display';
import AudioBars from '@/components/common/media-content/audio-bars';
import VideoViewer from '@/components/common/media-content/video-viewer';
import { SafeImage } from '@/components/common/safe-image';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import cn from '@/utils/merge-class-names';

interface Props {
  className?: string;
  type: ConversationMessageContentType;
  src: string;
}

export default function MediaPreviewContent({ type, src, className }: Props) {
  let content: ReactNode;

  switch (type) {
    case ConversationMessageContentType.Image:
      content = (
        <>
          <SafeImage
            src={src}
            className={cn('size-full bg-cover rounded', className)}
            alt='media preview content'
          />
          <Gallery
            variant='Bold'
            className='size-8 p-1.5 absolute bottom-0 start-0 text-neutral-0'
          />
        </>
      );
      break;
    case ConversationMessageContentType.Video:
      content = (
        <>
          <VideoViewer
            src={src}
            respectRatio={false}
            preload='metadata'
            className={cn('size-full object-cover bg-cover pointer-events-none rounded', className)}
            autoPlay={false}
            controls
          />
          <Video
            variant='Bold'
            className='size-8 p-1.5 absolute bottom-0 start-0 text-neutral-0'
          />
        </>
      );
      break;
    case ConversationMessageContentType.File:
      {
        const [filename = '', fileSize = ''] = src.split('|');
        content = (
          <>
            <Document
              variant='Outline'
              className='size-7 p-1.5 absolute top-2 start-0 text-neutral-700'
            />
            <div className={cn('p-2 pt-8 size-full rounded overflow-hidden', className)}>
              <span className='break-all line-clamp-4 overflow-hidden text-ellipsis text-sm leading-6'>{filename}</span>

              {fileSize && <span className='text-sm'>{fileSize}</span>}
            </div>
          </>
        );
      }
      break;
    case ConversationMessageContentType.Audio:
      {
        const duration = Number.parseInt(src.split('|').at(-1) ?? '0');
        content = (
          <div className={cn('flex items-center justify-center size-full p-4 relative', className)}>
            {/* 语音图标 - 整个卡片的左下角 */}
            <div className='absolute bottom-2 left-2 z-10'>
              <AudioIcon className='size-4' />
            </div>
            <div className='bg-neutral-0 rounded-2xl px-4 py-3 border-neutral-200 w-full max-w-[200px] flex items-center justify-between'>
              <AudioBars
                duration={duration}
                count={8}
                className='bg-neutral-600 flex-shrink-0'
              />
              <div className='text-body2 desktop:text-body3 text-body font-medium'>
                <DurationDisplay
                  compact={true}
                  withUnits={false}
                  duration={duration}
                />
              </div>
            </div>
          </div>
        );
      }
      break;
    default:
      content = (
        <div className={cn('p-2 pt-8 size-full rounded overflow-hidden', className)}>
          <div className='break-all line-clamp-4 overflow-hidden text-ellipsis text-sm leading-6'>{src}</div>
        </div>
      );
      break;
  }

  return (
    <div
      className={cn('relative aspect-square rounded overflow-hidden', {
        'border border-neutral-200': [
          ConversationMessageContentType.Text,
          ConversationMessageContentType.File,
        ].includes(type),
      })}>
      {content}
    </div>
  );
}
